import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, DownloadCloud } from 'lucide-react';

import { ApiError } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { FormField } from '@/features/patient-record/components/_shared/FormField';
import { useFocusOnMount } from '@/features/patient-record/components/_shared/useFocusOnMount';

import { useImportFromFhir } from '../useImportFromFhir';

interface ImportFromFhirDialogProps {
  readonly onClose: () => void;
}

/**
 * MRN-input dialog for the "Import from FHIR" action. Submits the trimmed
 * MRN with the current user id (auto-filled from the session) to
 * `POST /fhir/patients` and navigates to the upserted patient record on
 * success. Error messages are code-based — never surface `error.message`
 * or the MRN in the callout so PHI stays out of the DOM.
 */
export function ImportFromFhirDialog({
  onClose,
}: ImportFromFhirDialogProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const focusRef = useFocusOnMount<HTMLInputElement>();

  const [mrn, setMrn] = useState('');
  const importFromFhir = useImportFromFhir(auth.user?.id ?? null);

  const trimmedMrn = mrn.trim();
  const pending = importFromFhir.isPending;
  const canSubmit = trimmedMrn.length > 0 && !pending;

  const submit = (): void => {
    if (!canSubmit) return;
    importFromFhir.mutate(trimmedMrn, {
      onSuccess: (person) => {
        onClose();
        void navigate(`/patients/${encodeURIComponent(person.id)}`);
      },
    });
  };

  const errorMessage = pickErrorMessage(importFromFhir.error, t);

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      <DialogContent className="max-w-md" disableOutsideClose>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DownloadCloud className="h-4 w-4" aria-hidden="true" />
            {t('fhir.import.title')}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('fhir.import.description')}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >

          <FormField label={t('fhir.import.mrnLabel')}>
            <Input
              ref={focusRef}
              value={mrn}
              placeholder={t('fhir.import.mrnPlaceholder')}
              onChange={(event) => {
                setMrn(event.target.value);
              }}
              disabled={pending}
              autoComplete="off"
              spellCheck={false}
              className="h-9 text-sm"
              aria-invalid={errorMessage !== null}
            />
          </FormField>

          {errorMessage !== null && (
            <ErrorCallout message={errorMessage} />
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={pending}
            >
              {t('ui.dialog.close')}
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              loading={pending}
              loadingText={t('fhir.import.importing')}
            >
              {t('fhir.import.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function pickErrorMessage(
  error: unknown,
  t: (key: string) => string,
): string | null {
  if (error === null || error === undefined) return null;
  if (error instanceof ApiError) {
    switch (error.status) {
      case 404:
        return t('fhir.import.errors.notFound');
      case 300:
        return t('fhir.import.errors.notUnique');
      case 422:
        return t('fhir.import.errors.saveFailed');
      case 503:
        return t('fhir.import.errors.unavailable');
      case 400:
        return t('fhir.import.errors.notConfigured');
      case 403:
        return t('fhir.import.errors.forbidden');
      default:
        return t('fhir.import.errors.generic');
    }
  }
  return t('fhir.import.errors.generic');
}

function ErrorCallout({ message }: { readonly message: string }): ReactElement {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3"
    >
      <AlertTriangle
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive"
        aria-hidden="true"
      />
      <p className="text-xs text-destructive">{message}</p>
    </div>
  );
}
